// frontend/packages/business-console/src/services/buildFunnelService.js
//
// Work item 8.7 - thin wrapper over the Console-auth build-interview
// endpoints (`/api/v2/build/interview*`), plus the 8.10 draft review +
// approval endpoints (`/api/v2/build/draft|approve|request-changes`), the
// S7 go-live checklist (`/api/v2/build/go-live`), and the D23 v2 publish
// action (`/api/v2/build/publish`).
// Business context rides the x-business-id header via the
// @upryzze/services axios interceptor; callers never pass businessId.
//
// No SSE here (unlike the 5.10 author stream): an interview turn is a
// single short extraction, so plain JSON POSTs keep the F.1-F.4 SSE
// landmines out of this surface entirely.

import { api } from '@upryzze/services';

/**
 * Lightweight build-stage lookup for landing/routing. Returns
 * { hasActiveBuild, state, stage, routeSuffix } where stage is one of
 * 'interview' | 'review' | 'go_live' | 'live' | 'none' and routeSuffix is
 * the path under /business/:slug/ the customer should be on (or null).
 * Degrade-open: the backend returns stage 'none' on any error or for a
 * non-admin, so the Console lands on Home.
 */
export async function getBuildStatus() {
  const { data } = await api.get('/v2/build/status');
  return data;
}

/** Current interview state: steps, progress, draft data, flags. */
export async function getBuildInterview() {
  const { data } = await api.get('/v2/build/interview');
  return data;
}

/**
 * Submit one turn. Exactly one of `text` (freeform; server runs the
 * extractor) or `structured` (typed JSON; no AI call) per the step's
 * allowed modes.
 */
export async function submitBuildInterviewTurn({ stepId, text, structured }) {
  const body = { stepId };
  if (text !== undefined) body.text = text;
  if (structured !== undefined) body.structured = structured;
  const { data } = await api.post('/v2/build/interview/turn', body);
  return data;
}

/** Skip a skippable step (stores the honest empty default). */
export async function skipBuildInterviewStep(stepId) {
  const { data } = await api.post('/v2/build/interview/skip', { stepId });
  return data;
}

/** Step 10: freeze the brief and hand off to generation gating. */
export async function confirmBuildInterview() {
  const { data } = await api.post('/v2/build/interview/confirm', {});
  return data;
}

/** Start over: clear the draft answers; returns the fresh interview state. */
export async function resetBuildInterview() {
  const { data } = await api.post('/v2/build/interview/reset', {});
  return data;
}

// ── 8.10 draft review + approval (Approve gate v1) ───────────────

/**
 * The review page payload: { exists, state, previewUrl, ready,
 * canApprove, canRequestChanges, awaitingFounderReview, revising, inRescue }.
 */
export async function getBuildDraft() {
  const { data } = await api.get('/v2/build/draft');
  return data;
}

/** Customer signs off -> approved_pending_review (the founder gate). */
export async function approveBuildDraft() {
  const { data } = await api.post('/v2/build/approve', {});
  return data;
}

/**
 * Customer asks for changes (free-text note) -> in_revision / regen.
 * `images` is an optional array of reference image URLs (on the tenant's own
 * Cloudinary, uploaded via the shared direct-upload path) attached to the
 * request. Back-compat: omitting it posts a text-only request unchanged.
 */
export async function requestBuildChanges(note, images) {
  const body = { note };
  if (Array.isArray(images) && images.length > 0) body.images = images;
  const { data } = await api.post('/v2/build/request-changes', body);
  return data;
}

/**
 * Retry a failed generation (P4 self-heal). Only legal from
 * failed_generation; re-enters the generation worker (-> generating), which
 * advances to draft_deployed once the now-passing deploy is seen.
 */
export async function retryBuild() {
  const { data } = await api.post('/v2/build/retry', {});
  return data;
}

// ── S7 go-live checklist (read + confirm) ──────────────────────

/**
 * Go-live page payload: { available, state, apexDomain, includeWww,
 * prodEndpointHostname, previewUrl, prodUrl, records, validation, ready,
 * tokensStale, incomplete }.
 */
export async function getBuildGoLive() {
  const { data } = await api.get('/v2/build/go-live');
  return data;
}

/**
 * Publish (D23 v2): ends the free trial and starts the monthly plan, which is
 * what takes the site live on the customer's own domain. The first monthly
 * payment IS the go-live action; the paid-invoice webhook then promotes the
 * site. Returns { state, publishing } or { state, alreadyPublishing }.
 */
export async function publishBuildSite() {
  const { data } = await api.post('/v2/build/publish', {});
  return data;
}
